/**
 * Finance – personal transactions + budgets.
 * Doc: docs/overall-plan/02-MANGO-PERSONAL/finance/
 * Wired to GET/POST/DELETE /api/v1/finance/transactions, /budgets, and GET /summary.
 */
import React, { useEffect, useState } from 'react';
import { financeApi, type PersonalTransactionItem, type PersonalBudgetItem } from '../services/api';

interface BudgetStatus {
  category: string;
  monthlyLimit: number;
  spent: number;
  percentUsed: number;
}

interface FinanceSummary {
  totalSpend: number;
  spendByCategory: Record<string, number>;
  budgetStatus: BudgetStatus[];
  monthStart: string;
}

const formatCurrency = (n: number) =>
  new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(n);

const Finance: React.FC = () => {
  const [transactions, setTransactions] = useState<PersonalTransactionItem[]>([]);
  const [budgets, setBudgets] = useState<PersonalBudgetItem[]>([]);
  const [summary, setSummary] = useState<FinanceSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [showAddTransaction, setShowAddTransaction] = useState(false);
  const [newAmount, setNewAmount] = useState('');
  const [newCategory, setNewCategory] = useState('');
  const [newBusinessName, setNewBusinessName] = useState('');
  const [newTaxDeductible, setNewTaxDeductible] = useState(false);
  const [newDeductionType, setNewDeductionType] = useState('');
  const [submittingTransaction, setSubmittingTransaction] = useState(false);
  const [deletingTransactionId, setDeletingTransactionId] = useState<string | null>(null);

  const [showAddBudget, setShowAddBudget] = useState(false);
  const [newBudgetCategory, setNewBudgetCategory] = useState('');
  const [newBudgetLimit, setNewBudgetLimit] = useState('');
  const [submittingBudget, setSubmittingBudget] = useState(false);
  const [deletingBudgetId, setDeletingBudgetId] = useState<string | null>(null);

  const loadData = React.useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setError(null);
    try {
      const [txRes, budgetRes, summaryRes] = await Promise.all([
        financeApi.listTransactions(),
        financeApi.listBudgets(),
        financeApi.summary(),
      ]);
      if (txRes?.success) setTransactions(Array.isArray(txRes?.data) ? txRes.data : []);
      else setError(txRes?.error || 'Failed to load transactions');

      if (budgetRes?.success) setBudgets(Array.isArray(budgetRes?.data) ? budgetRes.data : []);
      else setError((prev) => prev || budgetRes?.error || 'Failed to load budgets');

      if (summaryRes?.success) setSummary(summaryRes?.data ?? null);
      else setError((prev) => prev || summaryRes?.error || 'Failed to load summary');
    } catch {
      setError('Failed to load finance data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAddTransaction = async () => {
    const amountNum = Number(newAmount);
    if (!newAmount || isNaN(amountNum) || !newCategory.trim()) return;
    setSubmittingTransaction(true);
    setError(null);
    try {
      const res = await financeApi.createTransaction({
        amount: amountNum,
        category: newCategory.trim(),
        businessName: newBusinessName.trim() || undefined,
        taxDeductible: newTaxDeductible,
        deductionType: newTaxDeductible && newDeductionType.trim() ? newDeductionType.trim() : undefined,
      });
      if (res?.success) {
        setNewAmount('');
        setNewCategory('');
        setNewBusinessName('');
        setNewTaxDeductible(false);
        setNewDeductionType('');
        setShowAddTransaction(false);
        loadData(true);
      } else setError(res?.error || 'Failed to add transaction');
    } catch {
      setError('Failed to add transaction');
    } finally {
      setSubmittingTransaction(false);
    }
  };

  const handleDeleteTransaction = async (id: string) => {
    setDeletingTransactionId(id);
    setError(null);
    try {
      const res = await financeApi.deleteTransaction(id);
      if (res?.success) loadData(true);
      else setError(res?.error || 'Failed to delete transaction');
    } catch {
      setError('Failed to delete transaction');
    } finally {
      setDeletingTransactionId(null);
    }
  };

  const handleAddBudget = async () => {
    const limitNum = Number(newBudgetLimit);
    if (!newBudgetCategory.trim() || !newBudgetLimit || isNaN(limitNum) || limitNum <= 0) return;
    setSubmittingBudget(true);
    setError(null);
    try {
      const res = await financeApi.createBudget({ category: newBudgetCategory.trim(), monthlyLimit: limitNum });
      if (res?.success) {
        setNewBudgetCategory('');
        setNewBudgetLimit('');
        setShowAddBudget(false);
        loadData(true);
      } else setError(res?.error || 'Failed to add budget');
    } catch {
      setError('Failed to add budget');
    } finally {
      setSubmittingBudget(false);
    }
  };

  const handleDeleteBudget = async (id: string) => {
    setDeletingBudgetId(id);
    setError(null);
    try {
      const res = await financeApi.deleteBudget(id);
      if (res?.success) loadData(true);
      else setError(res?.error || 'Failed to delete budget');
    } catch {
      setError('Failed to delete budget');
    } finally {
      setDeletingBudgetId(null);
    }
  };

  if (loading) {
    return (
      <div style={{ maxWidth: '700px', margin: '0 auto', padding: '24px' }}>
        <p>Loading…</p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '700px', margin: '0 auto', padding: '24px' }}>
      <div style={{
        background: 'linear-gradient(135deg, #232526 0%, #414345 100%)',
        borderRadius: '16px',
        padding: '24px',
        marginBottom: '24px',
        color: 'white',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }}>
          <div>
            <h1 style={{ margin: '0 0 8px 0', fontSize: '24px' }}>💰 Finance</h1>
            <p style={{ margin: 0, opacity: 0.95, fontSize: '14px' }}>
              Track spending, manage budgets, and stay on top of your money
            </p>
          </div>
          <button
            type="button"
            onClick={() => loadData(true)}
            disabled={refreshing}
            style={{
              padding: '8px 16px',
              background: 'rgba(255,255,255,0.2)',
              border: '1px solid rgba(255,255,255,0.4)',
              borderRadius: '8px',
              color: 'white',
              cursor: refreshing ? 'not-allowed' : 'pointer',
              fontSize: '14px',
            }}
          >
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      {error && <p style={{ color: '#b91c1c', marginBottom: '16px' }}>{error}</p>}

      {summary && (
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '24px',
          marginBottom: '24px',
          boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
        }}>
          <h2 style={{ margin: '0 0 16px 0', fontSize: '18px', color: '#333' }}>This month</h2>
          <div style={{ padding: '12px', background: '#f8f9fa', borderRadius: '8px', marginBottom: '16px' }}>
            <div style={{ fontSize: '12px', color: '#666' }}>Total spend</div>
            <div style={{ fontSize: '22px', fontWeight: 700, color: '#dc3545' }}>{formatCurrency(summary.totalSpend)}</div>
          </div>
          {summary.budgetStatus.length > 0 ? (
            <div>
              <h3 style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#333' }}>Budget status</h3>
              {summary.budgetStatus.map((b) => (
                <div key={b.category} style={{ marginBottom: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '4px' }}>
                    <strong>{b.category}</strong>
                    <span style={{ color: '#666' }}>
                      {formatCurrency(b.spent)} / {formatCurrency(b.monthlyLimit)} ({b.percentUsed}%)
                    </span>
                  </div>
                  <div style={{ height: '8px', background: '#e2e8f0', borderRadius: '4px', overflow: 'hidden' }}>
                    <div
                      style={{
                        height: '100%',
                        width: `${Math.min(b.percentUsed, 100)}%`,
                        background: b.percentUsed >= 100 ? '#dc3545' : b.percentUsed >= 80 ? '#fd7e14' : '#28a745',
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p style={{ margin: 0, fontSize: '13px', color: '#666' }}>No active budgets yet. Add one below.</p>
          )}
        </div>
      )}

      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '24px',
        marginBottom: '24px',
        boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h2 style={{ margin: 0, fontSize: '18px', color: '#333' }}>Transactions</h2>
          <button
            type="button"
            onClick={() => setShowAddTransaction((s) => !s)}
            style={{
              padding: '8px 16px',
              background: '#16a34a',
              border: 'none',
              borderRadius: '8px',
              color: 'white',
              cursor: 'pointer',
              fontSize: '14px',
            }}
          >
            {showAddTransaction ? 'Cancel' : 'Add Transaction'}
          </button>
        </div>

        {showAddTransaction && (
          <div style={{
            background: '#f8fafc',
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '16px',
            border: '1px solid #e2e8f0',
          }}>
            <input
              type="number"
              placeholder="Amount (required)"
              value={newAmount}
              onChange={(e) => setNewAmount(e.target.value)}
              style={{ width: '100%', padding: '10px 12px', marginBottom: '10px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px' }}
            />
            {budgets.length > 0 ? (
              <select
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
                style={{ width: '100%', padding: '10px 12px', marginBottom: '10px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px' }}
              >
                <option value="">Select category…</option>
                {budgets.map((b) => (
                  <option key={b.id} value={b.category}>{b.category}</option>
                ))}
                <option value="__other__">Other (type below)</option>
              </select>
            ) : null}
            {(budgets.length === 0 || newCategory === '__other__') && (
              <input
                type="text"
                placeholder="Category (required)"
                value={newCategory === '__other__' ? '' : newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
                style={{ width: '100%', padding: '10px 12px', marginBottom: '10px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px' }}
              />
            )}
            <input
              type="text"
              placeholder="Business name (optional, defaults to Manual entry)"
              value={newBusinessName}
              onChange={(e) => setNewBusinessName(e.target.value)}
              style={{ width: '100%', padding: '10px 12px', marginBottom: '10px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px' }}
            />
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', fontSize: '14px', color: '#333' }}>
              <input
                type="checkbox"
                checked={newTaxDeductible}
                onChange={(e) => setNewTaxDeductible(e.target.checked)}
              />
              Tax deductible
            </label>
            {newTaxDeductible && (
              <input
                type="text"
                placeholder="Deduction type (e.g. Home office, Travel)"
                value={newDeductionType}
                onChange={(e) => setNewDeductionType(e.target.value)}
                style={{ width: '100%', padding: '10px 12px', marginBottom: '10px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px' }}
              />
            )}
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                type="button"
                onClick={handleAddTransaction}
                disabled={submittingTransaction || !newAmount || !newCategory.trim() || newCategory === '__other__'}
                style={{
                  padding: '8px 16px',
                  background: '#16a34a',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  cursor: submittingTransaction ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                }}
              >
                {submittingTransaction ? 'Adding…' : 'Save'}
              </button>
              <button
                type="button"
                onClick={() => setShowAddTransaction(false)}
                style={{ padding: '8px 16px', background: '#64748b', border: 'none', borderRadius: '8px', color: 'white', cursor: 'pointer', fontSize: '14px' }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {transactions.length === 0 ? (
          <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '32px', textAlign: 'center', color: '#64748b', fontSize: '14px' }}>
            No transactions yet. Add one above.
          </div>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {transactions.map((t) => (
              <li
                key={t.id}
                style={{
                  padding: '12px 16px',
                  borderBottom: '1px solid #e2e8f0',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: '12px',
                }}
              >
                <div>
                  <strong>{t.category}</strong>
                  <span style={{ marginLeft: '8px', fontSize: '12px', color: '#64748b' }}>{t.businessName}</span>
                  {t.taxDeductible && (
                    <span style={{ marginLeft: '8px', fontSize: '11px', color: '#166534', background: '#dcfce7', padding: '2px 8px', borderRadius: '999px' }}>
                      Tax deductible
                    </span>
                  )}
                  <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#64748b' }}>
                    {new Date(t.transactionDate).toLocaleDateString()}
                  </p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <strong style={{ fontSize: '15px', color: '#333' }}>{formatCurrency(t.amount)}</strong>
                  <button
                    type="button"
                    onClick={() => handleDeleteTransaction(t.id)}
                    disabled={deletingTransactionId === t.id}
                    style={{
                      padding: '6px 12px',
                      background: '#dc2626',
                      border: 'none',
                      borderRadius: '6px',
                      color: 'white',
                      cursor: deletingTransactionId === t.id ? 'not-allowed' : 'pointer',
                      fontSize: '12px',
                    }}
                  >
                    {deletingTransactionId === t.id ? '…' : 'Delete'}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '24px',
        marginBottom: '24px',
        boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h2 style={{ margin: 0, fontSize: '18px', color: '#333' }}>Budgets</h2>
          <button
            type="button"
            onClick={() => setShowAddBudget((s) => !s)}
            style={{
              padding: '8px 16px',
              background: '#16a34a',
              border: 'none',
              borderRadius: '8px',
              color: 'white',
              cursor: 'pointer',
              fontSize: '14px',
            }}
          >
            {showAddBudget ? 'Cancel' : 'Add Budget'}
          </button>
        </div>

        {showAddBudget && (
          <div style={{
            background: '#f8fafc',
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '16px',
            border: '1px solid #e2e8f0',
          }}>
            <input
              type="text"
              placeholder="Category (required)"
              value={newBudgetCategory}
              onChange={(e) => setNewBudgetCategory(e.target.value)}
              style={{ width: '100%', padding: '10px 12px', marginBottom: '10px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px' }}
            />
            <input
              type="number"
              placeholder="Monthly limit (required)"
              value={newBudgetLimit}
              onChange={(e) => setNewBudgetLimit(e.target.value)}
              style={{ width: '100%', padding: '10px 12px', marginBottom: '10px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px' }}
            />
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                type="button"
                onClick={handleAddBudget}
                disabled={submittingBudget || !newBudgetCategory.trim() || !newBudgetLimit}
                style={{
                  padding: '8px 16px',
                  background: '#16a34a',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  cursor: submittingBudget ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                }}
              >
                {submittingBudget ? 'Adding…' : 'Save'}
              </button>
              <button
                type="button"
                onClick={() => setShowAddBudget(false)}
                style={{ padding: '8px 16px', background: '#64748b', border: 'none', borderRadius: '8px', color: 'white', cursor: 'pointer', fontSize: '14px' }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {budgets.length === 0 ? (
          <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '32px', textAlign: 'center', color: '#64748b', fontSize: '14px' }}>
            No budgets yet. Add one above.
          </div>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {budgets.map((b) => (
              <li
                key={b.id}
                style={{
                  padding: '12px 16px',
                  borderBottom: '1px solid #e2e8f0',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: '12px',
                }}
              >
                <div>
                  <strong>{b.category}</strong>
                  <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#64748b' }}>
                    {formatCurrency(b.currentSpending)} / {formatCurrency(b.monthlyLimit)}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => handleDeleteBudget(b.id)}
                  disabled={deletingBudgetId === b.id}
                  style={{
                    padding: '6px 12px',
                    background: '#dc2626',
                    border: 'none',
                    borderRadius: '6px',
                    color: 'white',
                    cursor: deletingBudgetId === b.id ? 'not-allowed' : 'pointer',
                    fontSize: '12px',
                  }}
                >
                  {deletingBudgetId === b.id ? '…' : 'Delete'}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default Finance;
